/**
 * Scenarios page state — a reducer port of the prototype's mutable `state`, extended for the
 * five-lever model and the wire's `ScenarioPatch`.
 *
 * All arithmetic lives in `@/lib/data/scenario.ts` (`runScenario`, `buildClaimMap`, …); this
 * file only holds the interactive state and the mapping to/from the agent's snake_case wire
 * shape (`stream.ts`). `resolve` is the pure local port of `resolveCore` (`agent-resolve.ts`) —
 * it needs the live `ScenarioContext` (families/model/skus), which the reducer can't fetch
 * itself, so the `resolve` action carries it alongside the intent, same pattern as a thunk.
 */

import {
  neutralScenarioParams,
  type Scenario5LeverId,
  type ScenarioContext,
  type ScenarioParams,
} from "@/lib/data";

import { resolveScenario } from "./agent-resolve";
import type { ScenarioPatch, ScenarioStatePayload } from "./stream";

export interface SavedScenario {
  name: string;
  params: ScenarioParams;
}

export type ViewModal = "business" | "customers" | "channels" | "operations" | null;
export type FilterState = { sys: "aframe" | "p2l" | "perfect"; mis: boolean } | null;

export interface ScenariosState {
  draft: ScenarioParams;
  scenarios: SavedScenario[];
  /** Which case the four views describe — `-1` = the live current state. */
  viewSel: number;
  filter: FilterState;
  /** Which lever's detail table is open in the drawer. */
  drawer: Scenario5LeverId | null;
  /** Which lever is being dragged, for the cause→effect highlight — cleared after ~1.6s. */
  hot: Scenario5LeverId | null;
  chatOn: boolean;
  vmodal: ViewModal;
  resolveNote: string | null;
  resolveTone: "ok" | "warn" | "";
}

export function initScenariosState(year: number): ScenariosState {
  return {
    draft: neutralScenarioParams(year),
    scenarios: [],
    viewSel: -1,
    filter: null,
    drawer: null,
    hot: null,
    chatOn: false,
    vmodal: null,
    resolveNote: null,
    resolveTone: "",
  };
}

export type ScenariosAction =
  | { type: "year"; year: number }
  | { type: "relo"; threshold: number; byAgent?: boolean }
  | { type: "toggleReloExcluded"; familyId: string }
  | { type: "pack"; count: number; byAgent?: boolean }
  | { type: "togglePackExcluded"; familyId: string }
  | { type: "conv"; count: number; byAgent?: boolean }
  | { type: "toggleConvExcluded"; familyId: string }
  | { type: "capSystem"; system: ScenarioParams["capSystem"] }
  | { type: "capUnits"; units: number; byAgent?: boolean }
  | { type: "pace"; pct: number; byAgent?: boolean }
  | { type: "filter"; filter: FilterState }
  | { type: "drawer"; drawer: Scenario5LeverId | null }
  | { type: "hot"; hot: Scenario5LeverId | null }
  | { type: "chat"; on: boolean }
  | { type: "vmodal"; vmodal: ViewModal }
  | { type: "dismissNote" }
  | { type: "save" }
  | { type: "selectScenario"; index: number }
  | { type: "reset"; year: number }
  | { type: "resolve"; ctx: ScenarioContext }
  | { type: "applyPatch"; patch: ScenarioPatch }
  /** Full restore (e.g. the chat's "revert to previous scenario") — unlike `applyPatch`, this
   *  CAN clear `capSystem` back to `null`, because it's a whole-scenario overwrite the user
   *  asked for directly, not an agent patch bound by the wire's "never clears cap_system" rule. */
  | { type: "restore"; payload: ScenarioStatePayload };

const LEVER5_IDS: readonly Scenario5LeverId[] = ["relo", "pack", "conv", "cap", "pace"];
function isLever5Id(id: string): id is Scenario5LeverId {
  return (LEVER5_IDS as readonly string[]).includes(id);
}

/** Manually moving a lever clears ITS agent tag and the whole resolve note — the note describes
 *  a bundle of settings, and one of them just changed by hand (build handover §9). */
function clearAgentTag(draft: ScenarioParams, lever: Scenario5LeverId): ScenarioParams["agentSet"] {
  return draft.agentSet.filter((id) => id !== lever);
}

export function scenariosReducer(state: ScenariosState, action: ScenariosAction): ScenariosState {
  switch (action.type) {
    case "year":
      return { ...state, draft: { ...state.draft, year: action.year } };
    case "relo": {
      const agentSet = action.byAgent ? state.draft.agentSet : clearAgentTag(state.draft, "relo");
      return {
        ...state,
        draft: { ...state.draft, reloThreshold: action.threshold, agentSet },
        resolveNote: action.byAgent ? state.resolveNote : null,
      };
    }
    case "toggleReloExcluded": {
      const cur = state.draft.reloExcluded;
      const next = cur.includes(action.familyId)
        ? cur.filter((id) => id !== action.familyId)
        : [...cur, action.familyId];
      return {
        ...state,
        draft: { ...state.draft, reloExcluded: next, agentSet: clearAgentTag(state.draft, "relo") },
        resolveNote: null,
      };
    }
    case "pack": {
      const agentSet = action.byAgent ? state.draft.agentSet : clearAgentTag(state.draft, "pack");
      return {
        ...state,
        draft: { ...state.draft, packCount: action.count, agentSet },
        resolveNote: action.byAgent ? state.resolveNote : null,
      };
    }
    case "togglePackExcluded": {
      const cur = state.draft.packExcluded;
      const next = cur.includes(action.familyId)
        ? cur.filter((id) => id !== action.familyId)
        : [...cur, action.familyId];
      return {
        ...state,
        draft: { ...state.draft, packExcluded: next, agentSet: clearAgentTag(state.draft, "pack") },
        resolveNote: null,
      };
    }
    case "conv": {
      const agentSet = action.byAgent ? state.draft.agentSet : clearAgentTag(state.draft, "conv");
      return {
        ...state,
        draft: { ...state.draft, convCount: action.count, agentSet },
        resolveNote: action.byAgent ? state.resolveNote : null,
      };
    }
    case "toggleConvExcluded": {
      const cur = state.draft.convExcluded;
      const next = cur.includes(action.familyId)
        ? cur.filter((id) => id !== action.familyId)
        : [...cur, action.familyId];
      return {
        ...state,
        draft: { ...state.draft, convExcluded: next, agentSet: clearAgentTag(state.draft, "conv") },
        resolveNote: null,
      };
    }
    case "capSystem":
      return {
        ...state,
        draft: {
          ...state.draft,
          capSystem: action.system,
          // Choosing a system after a run of 0 units gives the lever an immediate effect —
          // matches the prototype's own `if(state.p4units===0) setP('p4',1)`.
          capUnits: action.system && state.draft.capUnits === 0 ? 1 : state.draft.capUnits,
          agentSet: clearAgentTag(state.draft, "cap"),
        },
        resolveNote: null,
      };
    case "capUnits": {
      const agentSet = action.byAgent ? state.draft.agentSet : clearAgentTag(state.draft, "cap");
      return {
        ...state,
        draft: { ...state.draft, capUnits: action.units, agentSet },
        resolveNote: action.byAgent ? state.resolveNote : null,
      };
    }
    case "pace": {
      const agentSet = action.byAgent ? state.draft.agentSet : clearAgentTag(state.draft, "pace");
      return {
        ...state,
        draft: { ...state.draft, pacePct: action.pct, agentSet },
        resolveNote: action.byAgent ? state.resolveNote : null,
      };
    }
    case "filter":
      return { ...state, filter: action.filter };
    case "drawer":
      return { ...state, drawer: action.drawer };
    case "hot":
      return { ...state, hot: action.hot };
    case "chat":
      return { ...state, chatOn: action.on };
    case "vmodal":
      return { ...state, vmodal: action.vmodal };
    case "dismissNote":
      return { ...state, resolveNote: null };
    case "save": {
      const scn = state.viewSel >= 0 ? state.scenarios[state.viewSel] : undefined;
      if (!scn && !hasEngagement(state.draft)) {
        return state;
      }
      const scenarios = [
        ...state.scenarios,
        { name: `Scenario ${state.scenarios.length + 1}`, params: state.draft },
      ];
      return { ...state, scenarios, viewSel: scenarios.length - 1 };
    }
    case "selectScenario":
      return { ...state, viewSel: action.index };
    case "reset":
      return initScenariosState(action.year);
    case "resolve": {
      const result = resolveScenario(state.draft, action.ctx);
      return {
        ...state,
        draft: result.params,
        resolveNote: result.note,
        resolveTone: result.tone,
      };
    }
    case "applyPatch":
      return { ...state, draft: applyPatchToParams(state.draft, action.patch) };
    case "restore":
      return { ...state, draft: wireToParams(action.payload) };
  }
}

/** The inverse of `paramsToWire` — used only by a full restore (see `ScenariosAction.restore`);
 *  ordinary agent patches go through `applyPatchToParams` instead. */
export function wireToParams(payload: ScenarioStatePayload): ScenarioParams {
  return {
    year: payload.year,
    reloThreshold: payload.relo_threshold,
    reloExcluded: [...payload.relo_excluded],
    packCount: payload.pack_count,
    packExcluded: [...payload.pack_excluded],
    convCount: payload.conv_count,
    convExcluded: [...payload.conv_excluded],
    capSystem: payload.cap_system,
    capUnits: payload.cap_units,
    pacePct: payload.pace_pct,
    agentSet: payload.agent_set.filter(isLever5Id),
  };
}

function hasEngagement(p: ScenarioParams): boolean {
  return (
    p.reloThreshold >= 0 ||
    p.packCount > 0 ||
    p.convCount > 0 ||
    (!!p.capSystem && p.capUnits > 0) ||
    p.pacePct > 0
  );
}

/** The agent applies whichever fields it named, absolutely, reusing the same clamps every
 *  on-screen control goes through — a patch is trusted no more than a click on the widget it
 *  stands in for. Fields the agent didn't mention are left untouched. */
export function applyPatchToParams(draft: ScenarioParams, patch: ScenarioPatch): ScenarioParams {
  let next = draft;
  if (patch.year !== undefined) {
    next = { ...next, year: patch.year };
  }
  if (patch.relo_threshold !== undefined) {
    next = { ...next, reloThreshold: Math.max(-1, Math.min(24, patch.relo_threshold)) };
  }
  if (patch.relo_excluded !== undefined) {
    next = { ...next, reloExcluded: [...patch.relo_excluded] };
  }
  if (patch.pack_count !== undefined) {
    next = { ...next, packCount: Math.max(0, Math.min(10, patch.pack_count)) };
  }
  if (patch.pack_excluded !== undefined) {
    next = { ...next, packExcluded: [...patch.pack_excluded] };
  }
  if (patch.conv_count !== undefined) {
    next = { ...next, convCount: Math.max(0, Math.min(8, patch.conv_count)) };
  }
  if (patch.conv_excluded !== undefined) {
    next = { ...next, convExcluded: [...patch.conv_excluded] };
  }
  // Never cleared back to null by a patch — see `ScenarioPatch`'s docstring in `stream.ts`.
  if (patch.cap_system) {
    next = { ...next, capSystem: patch.cap_system };
  }
  if (patch.cap_units !== undefined) {
    next = { ...next, capUnits: Math.max(0, Math.min(6, patch.cap_units)) };
  }
  if (patch.pace_pct !== undefined) {
    next = { ...next, pacePct: Math.max(0, Math.min(100, patch.pace_pct)) };
  }
  if (patch.agent_set !== undefined) {
    next = { ...next, agentSet: patch.agent_set.filter(isLever5Id) };
  }
  return next;
}

/** The wire shape the agent endpoint takes — sent fresh every turn since the endpoint is
 *  stateless. */
export function paramsToWire(params: ScenarioParams): ScenarioStatePayload {
  return {
    year: params.year,
    relo_threshold: params.reloThreshold,
    relo_excluded: params.reloExcluded,
    pack_count: params.packCount,
    pack_excluded: params.packExcluded,
    conv_count: params.convCount,
    conv_excluded: params.convExcluded,
    cap_system: params.capSystem,
    cap_units: params.capUnits,
    pace_pct: params.pacePct,
    agent_set: params.agentSet,
  };
}
